import type { MinifiedGlobalPageSettings } from '~~/lib/ContentfulGraphqlService';

type ShopConfig = NonNullable<MinifiedGlobalPageSettings['shopConfig']>;

function collectPageSlugs(entries: Array<string | null | undefined>): string[] {
  return entries.filter(
    (slug): slug is string => typeof slug === 'string' && slug.length > 0
  );
}

/**
 * Cart, checkout and confirmation slugs from Contentful `shopConfig`.
 */
export function getCheckoutPageSlugs(
  shopConfig: ShopConfig | null | undefined
): string[] {
  if (!shopConfig) {
    return [];
  }

  return collectPageSlugs([
    shopConfig.cartPage?.slug,
    shopConfig.requestCartPage?.slug,
    shopConfig.cartCheckoutPage?.slug,
    shopConfig.requestCartCheckoutPage?.slug,
    shopConfig.cartConfirmationPage?.slug,
    shopConfig.requestCartConfirmationPage?.slug
  ]);
}

/**
 * Customer account area slugs from Contentful `shopConfig`.
 *
 * Contentful setup for onboarding preferences (CMS, not code):
 * - `shopConfig.customerAccountOnboardingPreferencesPage.slug` (e.g. `konto/onboarding`)
 * - Account page with `pageLayout: Two-Column-Layout` and a technicalComponent
 *   of type `customerAccountOnboardingPreferences`
 * - Secondary navigation link in `$secondaryNavigation.fields.linksForSecondaryNavigation`
 */
export function getAccountAreaPageSlugs(
  shopConfig: ShopConfig | null | undefined
): string[] {
  if (!shopConfig) {
    return [];
  }

  return collectPageSlugs([
    shopConfig.customerAccountAddressesPage?.slug,
    shopConfig.customerAccountOrdersPage?.slug,
    shopConfig.customerAccountOverviewPage?.slug,
    shopConfig.customerAccountProfilePage?.slug,
    shopConfig.customerAccountRequestsPage?.slug,
    shopConfig.customerAccountOnboardingPreferencesPage?.slug
  ]);
}

/**
 * Contentful shop page slugs that require a loaded Magento `storeConfig`.
 */
export function getShopContextSlugs(
  shopConfig: ShopConfig | null | undefined
): string[] {
  if (!shopConfig) {
    return [];
  }

  return collectPageSlugs([
    ...getCheckoutPageSlugs(shopConfig),
    ...getAccountAreaPageSlugs(shopConfig),
    shopConfig.productComparePage?.slug
  ]);
}
